import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, addQuarters, subQuarters, addYears, subYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, eachDayOfInterval } from 'date-fns';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, Loader2, CalendarIcon, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useFilters } from '@/contexts/FilterContext';
import { useTimezone } from '@/hooks/use-timezone';
import { CalendarView, Resource, Booking, Absence, BankHoliday } from '@/types';
import { BryntumSchedulerTimeline, BryntumSchedulerTimelineHandle, TimelineGroupingMode } from './BryntumSchedulerTimeline';
import { ComprehensiveBookingModal } from './ComprehensiveBookingModal';
import { CalendarFilterDrawer, CalendarFilterDrawerTrigger } from './CalendarFilterDrawer';
import { BookingDragConfirmDialog } from './BookingDragConfirmDialog';
import { DeleteBookingConfirmDialog } from './DeleteBookingConfirmDialog';
import { EditBookingConfirmDialog } from './EditBookingConfirmDialog';
import { CurrentTimezoneSelector } from './CurrentTimezoneSelector';
import { QuickAddBookingModal, QuickAddBookingData } from './QuickAddBookingModal';
import { MoveBookingDialog } from '@/components/booking/MoveBookingDialog';
import { AIBatchSuggestModal } from '@/components/unallocated/AIBatchSuggestModal';
import { DataModeToggle } from '@/components/DataModeToggle';
import { useCompany } from '@/contexts/CompanyContext';
import { useUser } from '@/contexts/UserContext';
import { useBookingDrag } from '@/hooks/use-booking-drag';
import { useDateRangeBookings } from '@/hooks/use-date-range-bookings';
import { convertBookingTimezone } from '@/utils/timezone-utils';
import { toast as sonnerToast } from 'sonner';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import { HeatmapLegend } from './HeatmapLegend';
import { BurnoutRiskPanelWrapper } from './BurnoutRiskPanel';
import { WhatIfScenario } from '@/types';
import { useConflictResolutionSettings } from '@/hooks/useConflictResolutionSettings';

// Helper function to format a Date as ISO-like string using LOCAL time values
// NO "Z" suffix - this prevents JavaScript from interpreting as UTC and applying timezone offset
const formatDateAsLocalISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  // No Z suffix to prevent UTC interpretation
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};

interface ResourceCalendarProps {
  initialHeatmapMode?: boolean;
  /** What-if scenario handed off from the heatmap panel — activates what-if mode */
  whatIfScenario?: WhatIfScenario | null;
  /** Called when the scenario is committed (true) or discarded (false) */
  onWhatIfComplete?: (navigateBackToHeatmap?: boolean) => void;
  /** Heatmap-mode only: called when the burnout panel wants to navigate to calendar */
  onNavigateToCalendarWithWhatIf?: (scenario: WhatIfScenario) => void;
}

export function ResourceCalendar({
  initialHeatmapMode = false,
  whatIfScenario,
  onWhatIfComplete,
  onNavigateToCalendarWithWhatIf,
}: ResourceCalendarProps) {
  const { calendarView, setCalendarView, isLoading: isContextLoading, isGroupsLoading, refreshResourcesAndGroups } = useScheduler();
  const { resources, bookings: allBookings, projectDetails, costCentres, schedulerGroups, settings, addBooking, createBatchBookings, updateBooking, deleteBooking, currentMode } = useScreenData('calendar');
  const { applyFilters, setFilterStats, hasActiveFilters, filters, hasActiveAdvancedFilters, advancedFilters, showFilteredBookings, updateFilter } = useFilters();
  const { hasCompanyAccess, isLoading: isCompanyLoading } = useCompany();
  const { user } = useUser();
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Local state for pending recurring bookings (optimistic UI, no API calls)
  // Declared early because it's used in the bookings useMemo below
  const [pendingRecurringBookings, setPendingRecurringBookings] = useState<Booking[]>([]);

  // Loading overlay state for timeline during add/update booking
  const [isSavingBooking, setIsSavingBooking] = useState(false);

  // Re-fetch resources & scheduler groups every time the Planner tab is
  // activated (i.e. this component mounts).  This ensures that company
  // access changes made by an admin on a different page are reflected
  // immediately when the user navigates back to the Planner.
  useEffect(() => {
    refreshResourcesAndGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  const {
    bookings: dateRangeBookings,
    refetchLiveBookings,
    isLoading: isLoadingBookings,
    dateRange,
  } = useDateRangeBookings({
    calendarView,
    selectedDate,
    allBookings,
    mode: currentMode,
  });

  // Merge real bookings with pending recurring bookings for display
  const bookings = useMemo(() => [
    ...dateRangeBookings,
    ...pendingRecurringBookings,
  ], [dateRangeBookings, pendingRecurringBookings]);
  const [zoomLevel, setZoomLevel] = useState(() => calendarView === 'day' ? 1 : 0); // Day view defaults to 1 (15-min), all others default to 0
  const [isFilterTrayOpen, setIsFilterTrayOpen] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingModalMode, setBookingModalMode] = useState<'create' | 'edit'>('create');
  const [editingBooking, setEditingBooking] = useState<Booking | undefined>(undefined);
  const [preselectedResource, setPreselectedResource] = useState<Resource | undefined>(undefined);
    const [preselectedDate, setPreselectedDate] = useState<Date | undefined>(undefined);
    const [preselectedEndDate, setPreselectedEndDate] = useState<Date | undefined>(undefined);
    const [preselectedStartTime, setPreselectedStartTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
    const [preselectedEndTime, setPreselectedEndTime] = useState<{ hour: number; minute: number } | undefined>(undefined);
  const [preselectedBookingData, setPreselectedBookingData] = useState<Partial<Booking> | undefined>(undefined);
  
  // Quick Add Booking modal state
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [quickAddPreselectedDate, setQuickAddPreselectedDate] = useState<Date | undefined>(undefined);
  const [quickAddPreselectedResource, setQuickAddPreselectedResource] = useState<Resource | undefined>(undefined);
  const [quickAddDefaultStartTime, setQuickAddDefaultStartTime] = useState<string>('09:00');
  const [quickAddDefaultEndTime, setQuickAddDefaultEndTime] = useState<string>('17:00');
  
  // Use cascading timezone system (user override or system default)
  const { timezone: effectiveTimezone, setUserTimezone } = useTimezone();
  
  // Update timezone when effective timezone changes (from user override or system default)
  // Copied booking state for paste functionality
  const [copiedBooking, setCopiedBooking] = useState<Booking | null>(null);
  
  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  
  // Edit confirmation dialog state for recurring bookings
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState<Booking | null>(null);
  // Tracks whether the user chose "this booking" or "all future bookings" in the edit dialog
  const [editScope, setEditScope] = useState<'this' | 'all-future' | null>(null);

  // Move booking dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [bookingToMove, setBookingToMove] = useState<Booking | null>(null);

  // Resolve conflict — batch assignment dialog state
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [bookingsToResolve, setBookingsToResolve] = useState<Booking[]>([]);

  
  // Timezone state - initialize based on whether timezones are enabled
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    settings.enableTimezones ? (settings.defaultTimezone || 'Europe/London') : 'Europe/London'
  );
  
  // Update timezone when settings or effective timezone changes
  // When timezone feature is disabled, always use Europe/London so no offset is applied
  // When enabled, use the effective timezone (user override or system default)
  useEffect(() => {
    if (!settings.enableTimezones) {
      setSelectedTimezone('Europe/London');
      return;
    }
    const tz = effectiveTimezone || settings.defaultTimezone || 'Europe/London';
    setSelectedTimezone(tz);
  }, [effectiveTimezone, settings.defaultTimezone, settings.enableTimezones]);
  
  // Update quick add default times from business hours settings
  useEffect(() => {
    const formatTime = (hour: number) => {
      return `${String(hour).padStart(2, '0')}:00`;
    };
    
    if (settings.businessHoursStart !== undefined) {
      setQuickAddDefaultStartTime(formatTime(settings.businessHoursStart));
    }
    if (settings.businessHoursEnd !== undefined) {
      setQuickAddDefaultEndTime(formatTime(settings.businessHoursEnd));
    }
  }, [settings.businessHoursStart, settings.businessHoursEnd]);
  
  
  // Date picker popover state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Normalize legacy timeline view values to current TimelineGroupingMode values
  const normalizeGroupingMode = (value: string | undefined): TimelineGroupingMode => {
    if (!value) return 'resource';
    const normalized = value.toLowerCase();
    if (normalized === 'resource') return 'resource';
    if (normalized === 'project & resource' || normalized === 'project-resource') return 'project-resource';
    if (normalized === 'role & resource' || normalized === 'role-resource') return 'role-resource';
    if (normalized === 'customer') return 'customer';
    return 'resource';
  };

  // Timeline grouping mode state - initialize from saved settings default
  const [groupingMode, setGroupingMode] = useState<TimelineGroupingMode>(
    normalizeGroupingMode(settings.timelineDefaultView)
  );
  
  // Sync grouping mode when settings.timelineDefaultView changes
  useEffect(() => {
    if (settings.timelineDefaultView) {
      setGroupingMode(normalizeGroupingMode(settings.timelineDefaultView));
    }
  }, [settings.timelineDefaultView]);
  
  // Track whether user has manually changed the grouping to skip initial render
  const [hasUserChangedGrouping, setHasUserChangedGrouping] = useState(false);

  // Dynamically measure the Bryntum column header height so the grouping Select
  // can be perfectly vertically centred regardless of theme or time-axis row count.
  const [groupingSelectTop, setGroupingSelectTop] = useState<number>(14);
  useEffect(() => {
    const measure = () => {
      // The left-column header cell spans the full height of the time axis header
      const headerCell = document.querySelector('.b-grid-header-container .b-grid-cell');
      if (headerCell) {
        const h = (headerCell as HTMLElement).offsetHeight;
        if (h > 0) setGroupingSelectTop(Math.round((h - 32) / 2) + 65);
      }
    };
    // Try immediately, then retry as Bryntum renders asynchronously
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    const timer = setTimeout(measure, 500);
    const timer2 = setTimeout(measure, 1500);
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
      clearTimeout(timer2);
      observer.disconnect();
    };
  }, []);

  // Refetch bookings when grouping mode changes so the new view has fresh data
  useEffect(() => {
    if (hasUserChangedGrouping && currentMode === 'live') {
      refetchLiveBookings();
    }
  }, [groupingMode, hasUserChangedGrouping, currentMode, refetchLiveBookings]);

  // Heatmap mode state
  const [isHeatmapMode, setIsHeatmapMode] = useState(initialHeatmapMode);

  // When entering heatmap mode, always default to Month view
  useEffect(() => {
    if (initialHeatmapMode) {
      setCalendarView('month');
    }
  }, [initialHeatmapMode, setCalendarView]);

  // ── What-if mode ─────────────────────────────────────────────────────────
  // When a scenario arrives from the heatmap panel, navigate the calendar to
  // the target week and switch to week view so both booking rows are visible.
  useEffect(() => {
    if (whatIfScenario) {
      setSelectedDate(whatIfScenario.targetWeekStart);
      setCalendarView('week');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario]);

  const handleWhatIfCommit = useCallback(async () => {
    if (!whatIfScenario) return;

    if (whatIfScenario.type === 'rebalance') {
      // ── Persist the move: reassign every at-risk booking to the candidate ──
      const bookingIds = whatIfScenario.atRiskBookingIds ?? [];
      const targetResourceId = whatIfScenario.candidateResourceId;
      if (bookingIds.length > 0 && targetResourceId) {
        await Promise.all(
          bookingIds.map((id) =>
            updateBooking(id, { resourceId: String(targetResourceId) })
          )
        );
      }

      const project = whatIfScenario.projectName ?? 'Project';
      const candidate = whatIfScenario.candidateResourceName ?? 'resource';
      const weekLabel = whatIfScenario.affectedWeekStarts?.[0]
        ? `W${Math.ceil((whatIfScenario.affectedWeekStarts[0].getTime() - new Date(whatIfScenario.affectedWeekStarts[0].getFullYear(), 0, 1).getTime()) / 604800000)}`
        : '';
      sonnerToast.success(`${project} moved to ${candidate}${weekLabel ? ` · ${weekLabel}` : ''} confirmed`, {
        duration: 4000,
      });
    } else {
      const name = whatIfScenario.freeResourceName ?? 'Resource';
      sonnerToast.success(`Booking assigned to ${name} · W${whatIfScenario.freeFromWeek ?? ''} confirmed`, {
        duration: 4000,
      });
    }

    onWhatIfComplete?.(true); // true = navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, onWhatIfComplete, updateBooking]);

  const handleWhatIfDiscard = useCallback(() => {
    onWhatIfComplete?.(true); // discard → also navigate back to heatmap
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onWhatIfComplete]);

  // Ref for Bryntum scheduler to revert events on discard
  const schedulerTimelineRef = useRef<BryntumSchedulerTimelineHandle>(null);

  // Ref to the outermost scheduler DOM element — used by BurnoutRiskPanel for DOM injection
  const schedulerDomRef = useRef<HTMLDivElement | null>(null);
  
  // Capacity risk feature flag
  const { whatIfEnabled } = useConflictResolutionSettings();

  // Drag and drop state
  const { cancelDrag } = useBookingDrag();
  const [showDragConfirm, setShowDragConfirm] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState<{
    booking: Booking;
    originalResource: Resource;
    newResource: Resource;
    originalStartTime: Date;
    originalEndTime: Date;
    newStartTime: Date;
    newEndTime: Date;
  } | null>(null);

  // Apply filters to resources and bookings
  const filteredData = useMemo(() => {
    const result = applyFilters(resources, bookings, settings, projectDetails, costCentres, dateRange);
    
    // Apply booking search query filter
    let searchFilteredBookings = result.filteredBookings;
    if (bookingSearchQuery.trim()) {
      const query = bookingSearchQuery.toLowerCase();
      
      searchFilteredBookings = result.filteredBookings.filter(booking => {
        // Get resource details
        const resource = resources.find(r => String(r.id) === String(booking.resourceId));
        const resourceName = resource?.name?.toLowerCase() || '';
        const resourceGroup = resource?.schedulerGroup?.toLowerCase() || '';
        const resourceDepartment = resource?.department?.toLowerCase() || '';
        
        // Get project details
        const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
        const projectCode = project?.billingCode?.toLowerCase() || '';
        const projectName = booking.projectName?.toLowerCase() || '';
        
        // Get cost centre details
        const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
        const costCentreCode = costCentre?.code?.toLowerCase() || '';
        const costCentreName = costCentre?.name?.toLowerCase() || '';
        
        // Get description
        const description = booking.description?.toLowerCase() || '';
        
        // Check if any field matches the search query
        return (
          resourceName.includes(query) ||
          resourceGroup.includes(query) ||
          resourceDepartment.includes(query) ||
          projectCode.includes(query) ||
          projectName.includes(query) ||
          costCentreCode.includes(query) ||
          costCentreName.includes(query) ||
          description.includes(query)
        );
      });
    }
    
    // If showFilteredBookings is true, include all bookings but mark filtered ones
    if (showFilteredBookings) {
      const filteredBookingIds = new Set(searchFilteredBookings.map(b => b.id));
      
      // Add isFiltered flag to all bookings
      const allBookingsWithFlag = bookings.map(booking => ({
        ...booking,
        isFiltered: !filteredBookingIds.has(booking.id)
      }));
      
      return {
        ...result,
        filteredBookings: allBookingsWithFlag
      };
    }
    
    return {
      ...result,
      filteredBookings: searchFilteredBookings
    };
  }, [resources, bookings, applyFilters, settings, projectDetails, costCentres, showFilteredBookings, currentMode, bookingSearchQuery, dateRange]);

  // Update filter stats in a separate effect to avoid setState during render
  useEffect(() => {
    setFilterStats(filteredData.stats);
  }, [filteredData.stats, setFilterStats]);

  // ── Resource Time Ranges ──────────────────────────────────────────────
  // Compute resourceTimeRanges here (absences, bank holidays, non-working
  // weekdays) and pass them down to BryntumSchedulerTimeline as a prop so
  // the timeline component stays presentation-only.
  // Weekends are intentionally excluded — no shading is applied for them.
  const resourceTimeRanges = useMemo(() => {
    const ranges: Array<{
      id: string;
      resourceId: string;
      name: string;
      startDate: Date;
      endDate: Date;
      timeRangeColor?: string;
      cls?: string;
      style?: string;
    }> = [];

    const filteredResources = filteredData.filteredResources;
    const filteredBookings = filteredData.filteredBookings;

    // Wait until resources have been loaded into state before computing
    // ranges.  This prevents premature empty ranges that never get
    // repopulated because the Bryntum store refresh already fired.
    if (filteredResources.length === 0) {
      return ranges;
    }

    const absences: Absence[] = mockAbsences;
    const bankHolidays: BankHoliday[] = mockBankHolidays;

    // Get colors from settings
    const absenceColor = settings.absenceColor || '#94a3b8';
    const publicHolidayColor = settings.publicHolidayColor || '#fbbf24';
    const businessHoursStart = settings.businessHoursStart || 9;
    const businessHoursEnd = settings.businessHoursEnd || 17;

    // Process absences (only if showAbsence is enabled)
    if (settings.showAbsence !== false) {
      absences.forEach((absence) => {
        const absenceStartDate = new Date(absence.startDate);
        const absenceEndDate = new Date(absence.endDate);

        const currentDate = new Date(absenceStartDate);
        while (currentDate <= absenceEndDate) {
          let rangeStart: Date;
          let rangeEnd: Date;

          switch (absence.type) {
            case 'full-day':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
              break;
            case 'half-day-am':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(businessHoursStart, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(12, 0, 0, 0);
              break;
            case 'half-day-pm':
              rangeStart = new Date(currentDate);
              rangeStart.setHours(12, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(businessHoursEnd, 0, 0, 0);
              break;
            case 'hourly':
              if (absence.startTime && absence.endTime) {
                const [startHour, startMin] = absence.startTime.split(':').map(Number);
                const [endHour, endMin] = absence.endTime.split(':').map(Number);
                rangeStart = new Date(currentDate);
                rangeStart.setHours(startHour, startMin, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(endHour, endMin, 0, 0);
              } else {
                rangeStart = new Date(currentDate);
                rangeStart.setHours(0, 0, 0, 0);
                rangeEnd = new Date(currentDate);
                rangeEnd.setHours(23, 59, 59, 999);
              }
              break;
            default:
              rangeStart = new Date(currentDate);
              rangeStart.setHours(0, 0, 0, 0);
              rangeEnd = new Date(currentDate);
              rangeEnd.setHours(23, 59, 59, 999);
          }

          if (groupingMode === 'project-resource') {
            const projectsForResource = new Set<string>();
            filteredBookings.forEach(booking => {
              if (String(booking.resourceId) === String(absence.resourceId) && booking.projectId) {
                projectsForResource.add(booking.projectId);
              }
            });
            projectsForResource.forEach(projectId => {
              ranges.push({
                id: `absence-${absence.id}-${projectId}-${currentDate.toISOString().split('T')[0]}`,
                resourceId: `${projectId}-${absence.resourceId}`,
                name: absence.reason || 'Absence',
                startDate: rangeStart,
                endDate: rangeEnd,
                timeRangeColor: absenceColor,
                cls: 'absence-range',
              });
            });
          } else if (groupingMode === 'role-resource') {
            const resource = filteredResources.find(r => String(r.id) === String(absence.resourceId));
            const roleName = resource?.role || 'Unassigned';
            ranges.push({
              id: `absence-${absence.id}-${roleName}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: `${roleName}-${absence.resourceId}`,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          } else {
            ranges.push({
              id: `absence-${absence.id}-${absence.resourceId}-${currentDate.toISOString().split('T')[0]}`,
              resourceId: absence.resourceId,
              name: absence.reason || 'Absence',
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: absenceColor,
              cls: 'absence-range',
            });
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
    }

    // Process bank holidays (only if showPublicHolidays is enabled)
    if (settings.showPublicHolidays !== false) {
      bankHolidays.forEach((holiday) => {
        const holidayDate = new Date(holiday.date);
        const rangeStart = new Date(holidayDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(holidayDate);
        rangeEnd.setHours(23, 59, 59, 999);

        if (groupingMode === 'project-resource') {
          const projectResourceCombinations = new Set<string>();
          filteredBookings.forEach(booking => {
            if (booking.projectId && booking.resourceId) {
              projectResourceCombinations.add(`${booking.projectId}-${booking.resourceId}`);
            }
          });
          projectResourceCombinations.forEach(combinedId => {
            ranges.push({
              id: `holiday-${holiday.id}-${combinedId}`,
              resourceId: combinedId,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else if (groupingMode === 'role-resource') {
          filteredResources.forEach((resource) => {
            const roleName = resource.role || 'Unassigned';
            ranges.push({
              id: `holiday-${holiday.id}-${roleName}-${resource.id}`,
              resourceId: `${roleName}-${resource.id}`,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        } else {
          filteredResources.forEach((resource) => {
            ranges.push({
              id: `holiday-${holiday.id}-${resource.id}`,
              resourceId: resource.id,
              name: holiday.name,
              startDate: rangeStart,
              endDate: rangeEnd,
              timeRangeColor: publicHolidayColor,
              cls: 'bank-holiday-range',
            });
          });
        }
      });
    }

    // ── Non-working weekdays ───────────────────────────────────────────

    // Determine visible date range
    let visibleStart: Date;
    let visibleEnd: Date;
    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();

    switch (calendarView) {
      case 'day':
        visibleStart = new Date(validDate);
        visibleStart.setHours(0, 0, 0, 0);
        visibleEnd = new Date(validDate);
        visibleEnd.setHours(23, 59, 59, 999);
        break;
      case 'week':
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
        break;
      case 'month':
        visibleStart = startOfMonth(validDate);
        visibleEnd = endOfMonth(validDate);
        break;
      case 'quarter':
        visibleStart = startOfQuarter(validDate);
        visibleEnd = endOfQuarter(validDate);
        break;
      case 'year':
        visibleStart = startOfYear(validDate);
        visibleEnd = endOfYear(validDate);
        break;
      default:
        visibleStart = startOfWeek(new Date(validDate), { weekStartsOn: 1 });
        visibleEnd = endOfWeek(new Date(validDate), { weekStartsOn: 1 });
    }

    const isVisibleStartValid = visibleStart instanceof Date && !isNaN(visibleStart.getTime());
    const isVisibleEndValid = visibleEnd instanceof Date && !isNaN(visibleEnd.getTime());
    if (!isVisibleStartValid || !isVisibleEndValid || visibleStart.getTime() >= visibleEnd.getTime()) {
      return ranges;
    }

    const visibleDays = eachDayOfInterval({ start: visibleStart, end: visibleEnd });
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    // Non-working weekdays: days where resource has 0 working hours.
    // Only weekdays (Mon-Fri) are checked — weekends are intentionally
    // excluded so they do not get any shading at all.
    filteredResources.forEach((resource) => {
      visibleDays.forEach((day) => {
        const dow = day.getDay();
        // Skip weekends — we only shade non-working *weekdays*
        if (dow === 0 || dow === 6) return;

        const dayKey = dayNames[dow];

        let workingHours: number;
        if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
          workingHours = resource.customWorkingProfile[dayKey] || 0;
        } else {
          workingHours = settings.globalWorkingProfile?.[dayKey] || 0;
        }

        if (workingHours === 0) {
          const rangeStart = new Date(day);
          rangeStart.setHours(0, 0, 0, 0);
          const rangeEnd = new Date(day);
          rangeEnd.setHours(23, 59, 59, 999);
          const dayStr = day.toISOString().split('T')[0];

          if (groupingMode === 'project-resource') {
            const projectsForResource = new Set<string>();
            filteredBookings.forEach(booking => {
              if (String(booking.resourceId) === String(resource.id) && booking.projectId) {
                projectsForResource.add(booking.projectId);
              }
            });
            if (projectsForResource.size === 0) {
              ranges.push({
                id: `non-working-${resource.id}-${dayStr}`,
                resourceId: String(resource.id),
                name: '',
                startDate: rangeStart,
                endDate: rangeEnd,
                cls: 'custom-style',
              });
            } else {
              projectsForResource.forEach(projectId => {
                ranges.push({
                  id: `non-working-${resource.id}-${projectId}-${dayStr}`,
                  resourceId: `${projectId}-${resource.id}`,
                  name: '',
                  startDate: rangeStart,
                  endDate: rangeEnd,
                  cls: 'custom-style',
                });
              });
            }
          } else if (groupingMode === 'role-resource') {
            const roleName = resource.role || 'Unassigned';
            ranges.push({
              id: `non-working-${resource.id}-${roleName}-${dayStr}`,
              resourceId: `${roleName}-${resource.id}`,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          } else {
            ranges.push({
              id: `non-working-${resource.id}-${dayStr}`,
              resourceId: resource.id,
              name: '',
              startDate: rangeStart,
              endDate: rangeEnd,
              cls: 'custom-style',
            });
          }
        }
      });
    });

    return ranges;
  }, [
    filteredData.filteredResources, filteredData.filteredBookings, settings.absenceColor,
    settings.publicHolidayColor,
    settings.showAbsence, settings.showPublicHolidays, settings.globalWorkingProfile,
    groupingMode, selectedDate, calendarView,
  ]);

  // ── Time Ranges (global, non-resource-specific) ──────────────────────
  // Non-business hours in Day view are rendered as global timeRanges
  // (column-spanning) rather than per-resource resourceTimeRanges.
  const timeRanges = useMemo(() => {
    const ranges: Array<{
      id: string;
      name: string;
      startDate: Date;
      endDate: Date;
      cls?: string;
    }> = [];

    if (calendarView !== 'day') return ranges;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;
    const visStartRaw = settings.visibleHoursStart ?? 7;
    const visEndRaw = settings.visibleHoursEnd ?? 19;

    const toHM = (v: number) => ({ h: Math.floor(v), m: Math.round((v - Math.floor(v)) * 60) });
    const bizStartHM = toHM(bizStartRaw);
    const bizEndHM = toHM(bizEndRaw);
    const visStartHM = toHM(visStartRaw);
    const visEndHM = toHM(visEndRaw);

    const validDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? selectedDate
      : new Date();
    const dayDate = new Date(validDate);

    // Before business hours: visStart → bizStart
    if (visStartRaw < bizStartRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(visStartHM.h, visStartHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(bizStartHM.h, bizStartHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-am',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    // After business hours: bizEnd → visEnd
    if (bizEndRaw < visEndRaw) {
      const rangeStart = new Date(dayDate);
      rangeStart.setHours(bizEndHM.h, bizEndHM.m, 0, 0);
      const rangeEnd = new Date(dayDate);
      rangeEnd.setHours(visEndHM.h, visEndHM.m, 0, 0);
      ranges.push({
        id: 'non-biz-pm',
        name: '',
        startDate: rangeStart,
        endDate: rangeEnd,
        cls: 'custom-style',
      });
    }

    return ranges;
  }, [
    settings.businessHoursStart, settings.businessHoursEnd,
    settings.visibleHoursStart, settings.visibleHoursEnd,
    selectedDate, calendarView,
  ]);

  // Initialize component
  useEffect(() => {
    console.log('ResourceCalendar: Initializing...');
    
    // Use setTimeout to ensure the component renders after the initial mount
    const timer = setTimeout(() => {
      setIsInitialized(true);
      console.log('ResourceCalendar: Initialized successfully');
    }, 0);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // ── Legacy localStorage cleanup ──────────────────────────────────────
  // Remove any leftover localStorage background task keys from the previous
  // client-side approach. All recurring booking creation is now handled
  // server-side via the batch API endpoint.
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('eb-bg-task-')) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleaned up ${keysToRemove.length} legacy localStorage task(s)`);
      }
    } catch { /* ignore */ }
  }, []);

  // Reset zoom level when changing views
  // Day view defaults to 1 (15-minute intervals), others default to 0
  useEffect(() => {
    setZoomLevel(calendarView === 'day' ? 1 : 0);
    if (calendarView === 'day') {
      setIsHeatmapMode(false);
    }
  }, [calendarView]);


  // ── What-if visual overlay ────────────────────────────────────────────────
  // Ghost tiles and source-booking highlighting are handled entirely inside
  // BryntumSchedulerTimeline via its whatIfScenario prop + eventStore API.
  // A lightweight CSS stylesheet is still injected here for the moving/ghost
  // CSS classes that Bryntum applies to the rendered event elements.

  // For assign-free: auto-open the booking modal with the free resource pre-selected
  const whatIfAssignOpenedRef = useRef(false);
  useEffect(() => {
    if (
      whatIfScenario?.type === 'assign-free' &&
      !whatIfAssignOpenedRef.current &&
      resources.length > 0
    ) {
      const freeResource = resources.find(
        (r) => String(r.id) === String(whatIfScenario.freeResourceId)
      );
      if (freeResource) {
        whatIfAssignOpenedRef.current = true;
        const tid = window.setTimeout(() => {
          setPreselectedResource(freeResource);
          setPreselectedDate(whatIfScenario.targetWeekStart);
          setBookingModalMode('create');
          setEditingBooking(undefined);
          setIsBookingModalOpen(true);
        }, 400);
        return () => window.clearTimeout(tid);
      }
    }
    if (!whatIfScenario) {
      whatIfAssignOpenedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatIfScenario, resources]);

  // Show loading state during initialization
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return subDays(prev, 1);
        case 'week':
          return subWeeks(prev, 1);
        case 'month':
          return subMonths(prev, 1);
        case 'quarter':
          return subQuarters(prev, 1);
        case 'year':
          return subYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      switch (calendarView) {
        case 'day':
          return addDays(prev, 1);
        case 'week':
          return addWeeks(prev, 1);
        case 'month':
          return addMonths(prev, 1);
        case 'quarter':
          return addQuarters(prev, 1);
        case 'year':
          return addYears(prev, 1);
        default:
          return prev;
      }
    });
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Get max zoom level based on current view
  const getMaxZoomLevel = () => {
    switch (calendarView) {
      case 'day':
        return 1; // Day view: 0 = hourly, 1 = 15 min (only 2 levels)
      case 'week':
        return 4; // Week view: 0 = daily, 1 = 12hr, 2 = 6hr, 3 = 3hr, 4 = 1hr
      default:
        return 4; // Other views can have up to 5 levels
    }
  };

  // Get zoom level labels based on current view
  const getZoomLabels = () => {
    switch (calendarView) {
      case 'day':
        return ['Min', 'Max']; // Only 2 levels for day view
      case 'week':
        return ['Min', 'Low', 'Med', 'High', 'Max'];
      default:
        return ['Min', 'Low', 'Med', 'High', 'Max'];
    }
  };

  const handleZoomIn = () => {
    const maxZoom = getMaxZoomLevel();
    setZoomLevel((prev) => Math.min(maxZoom, prev + 1));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(0, prev - 1));
  };

  const handleTimezoneChange = async (timezone: string) => {
    setSelectedTimezone(timezone);
    // Persist timezone change as user preference
    await setUserTimezone(timezone);
    sonnerToast.success('Timezone Updated', {
      description: `Timeline is now displaying times in ${timezone}. This will be used for new bookings.`,
    });
  };


  const handleCreateBooking = () => {
    // Reset preselected values when using the button
    setBookingModalMode('create');
    setEditingBooking(undefined);
    setPreselectedDate(undefined);
    setPreselectedEndDate(undefined);

    // If "Default to logged-in resource" is enabled, pre-select the current user's resource
    const shouldDefaultToLoggedIn = settings.userDefaultToLoggedInResource ?? settings.defaultToLoggedInResource;
    if (shouldDefaultToLoggedIn && user?.id) {
      const loggedInResource = resources.find(r => String(r.id) === String(user.id));
      setPreselectedResource(loggedInResource ?? undefined);
    } else {
      setPreselectedResource(undefined);
    }
    
    // Scenario 4a: If in day view, populate start/end times from business hours settings
    // Scenario 1: If not in day view, set duration type to 'full-day'
    if (calendarView === 'day') {
      // Day view: use business hours from admin settings
      const startHour = settings.businessHoursStart ?? 9;
      const endHour = settings.businessHoursEnd ?? 17;
      setPreselectedStartTime({ hour: startHour, minute: 0 });
      setPreselectedEndTime({ hour: endHour, minute: 0 });
      setPreselectedBookingData({ durationType: 'hourly' });
    } else {
      // Non-day view: full-day booking
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ durationType: 'full-day' });
    }
    
    setIsBookingModalOpen(true);
  };

  const openEditModal = (booking: Booking, scope: 'this' | 'all-future' | null) => {
    setEditScope(scope);
    setBookingModalMode('edit');
    setEditingBooking(booking);
    setPreselectedResource(undefined);
    setPreselectedDate(undefined);
    setPreselectedEndDate(undefined);
    setPreselectedStartTime(undefined);
    setPreselectedEndTime(undefined);
    setPreselectedBookingData(undefined);
    setIsBookingModalOpen(true);
  };

  const handleEditBooking = (booking: Booking) => {
    console.log('🔵 ResourceCalendar: handleEditBooking called with booking:', booking.id);

    // If the booking is part of a recurrence group, check if it has future bookings
    if (booking.recurrenceGroupId) {
      const bookingStart = new Date(booking.startTime);
      const futureBookingsInGroup = allBookings.filter(b => {
        if (b.recurrenceGroupId !== booking.recurrenceGroupId) return false;
        if (String(b.id) === String(booking.id)) return false;
        return new Date(b.startTime) > bookingStart;
      });

      if (futureBookingsInGroup.length === 0) {
        // Last occurrence — open directly in edit mode (no dialog)
        console.log('🔵 Last occurrence in recurrence group — opening edit directly');
        openEditModal(booking, 'this');
      } else {
        // Has future bookings — show confirmation dialog
        console.log(`🔵 Recurring booking has ${futureBookingsInGroup.length} future bookings — showing edit dialog`);
        setBookingToEdit(booking);
        setShowEditConfirm(true);
      }
    } else {
      // Non-recurring booking — open directly
      openEditModal(booking, null);
    }
  };

  const handleEditThisBooking = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      openEditModal(bookingToEdit, 'this');
    }
    setBookingToEdit(null);
  };

  const handleEditAllFutureBookings = () => {
    setShowEditConfirm(false);
    if (bookingToEdit) {
      openEditModal(bookingToEdit, 'all-future');
    }
    setBookingToEdit(null);
  };

  const handleCancelEditConfirm = () => {
    setShowEditConfirm(false);
    setBookingToEdit(null);
  };

  const handleDeleteBooking = (booking: Booking) => {
    setBookingToDelete(booking);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (bookingToDelete) {
      deleteBooking(bookingToDelete.id);
      sonnerToast.success('Booking Deleted', {
        description: 'The booking has been successfully deleted.',
      });
    }
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete only the selected recurring booking occurrence
  const handleDeleteOnlyThis = () => {
    if (bookingToDelete) {
      deleteBooking(bookingToDelete.id);
      sonnerToast.success('Booking Deleted', {
        description: 'The selected booking occurrence has been deleted.',
      });
    }
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Delete the selected booking and all future occurrences in the same recurrence group
  const handleDeleteAllFuture = async () => {
    if (!bookingToDelete || !bookingToDelete.recurrenceGroupId) return;

    const groupId = bookingToDelete.recurrenceGroupId;
    const selectedStartTime = new Date(bookingToDelete.startTime).getTime();

    // Find all bookings in the same recurrence group that are at or after the selected booking
    const futureBookings = allBookings.filter(
      b => b.recurrenceGroupId === groupId &&
           new Date(b.startTime).getTime() >= selectedStartTime
    );

    console.log(`🗑️ Deleting ${futureBookings.length} future recurring bookings from group ${groupId}`);

    let successCount = 0;
    let failCount = 0;
    for (const booking of futureBookings) {
      try {
        await deleteBooking(booking.id);
        successCount++;
      } catch (err) {
        console.error(`❌ Failed to delete booking ${booking.id}:`, err);
        failCount++;
      }
    }

    sonnerToast.success('Recurring Bookings Deleted', {
      description: `Successfully deleted ${successCount} booking${successCount !== 1 ? 's' : ''}.${failCount > 0 ? ` ${failCount} failed.` : ''}`,
    });

    if (currentMode === 'live') refetchLiveBookings();

    console.log(`✅ Recurring delete complete: ${successCount} succeeded, ${failCount} failed`);
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleMoveBooking = (booking: Booking) => {
    setBookingToMove(booking);
    setShowMoveDialog(true);
  };

  // ── Resolve Conflict via Batch Assignment Modal ────────────────────────────
  const handleResolveConflict = (cellBookings: Booking[]) => {
    setBookingsToResolve(cellBookings);
    setShowResolveDialog(true);
  };

  const handleResolveConfirm = async (assignments: { bookingId: string; resourceId: string }[]) => {
    let successCount = 0;
    for (const { bookingId, resourceId } of assignments) {
      try {
        await updateBooking(bookingId, { resourceId });
        successCount++;
      } catch { /* continue with remaining */ }
    }
    if (successCount > 0) {
      sonnerToast.success(`${successCount} conflict${successCount !== 1 ? 's' : ''} resolved`, {
        description: `${successCount} booking${successCount !== 1 ? 's' : ''} successfully reallocated.`,
      });
    }
    if (successCount < assignments.length) {
      sonnerToast.error(`${assignments.length - successCount} reassignment(s) failed`, {
        description: 'Please try again or edit those bookings manually.',
      });
    }
    setShowResolveDialog(false);
    setBookingsToResolve([]);
  };

  const handleConfirmMove = async (bookingId: string | number, newDate: Date, newResourceId: string) => {
    if (!bookingToMove) return;

    try {
      // Parse the original start and end times
      const oldStartTime = new Date(bookingToMove.startTime);
      const oldEndTime = new Date(bookingToMove.endTime);
      
      // Calculate the date difference (in days)
      const oldDate = new Date(oldStartTime);
      oldDate.setHours(0, 0, 0, 0);
      const targetDate = new Date(newDate);
      targetDate.setHours(0, 0, 0, 0);
      const dateDiff = targetDate.getTime() - oldDate.getTime();

      // Create new start and end times by adding the date difference
      const newStartTime = new Date(oldStartTime.getTime() + dateDiff);
      const newEndTime = new Date(oldEndTime.getTime() + dateDiff);

      const updatedBooking: Partial<Booking> = {
        ...bookingToMove,
        resourceId: newResourceId,
        startTime: newStartTime.toISOString(),
        endTime: newEndTime.toISOString(),
      };

      await updateBooking(String(bookingId), updatedBooking);
      if (currentMode === 'live') refetchLiveBookings();
      
      sonnerToast.success('Booking moved', {
        description: 'The booking has been successfully moved.',
      });
    } catch (error) {
      console.error('Error moving booking:', error);
      sonnerToast.error('Failed to move booking', {
        description: 'An error occurred while moving the booking.',
      });
    } finally {
      setShowMoveDialog(false);
      setBookingToMove(null);
    }
  };

  const handleCancelMove = () => {
    setShowMoveDialog(false);
    setBookingToMove(null);
  };

  const handleDuplicateBooking = (booking: Booking) => {
    // Create a copy of the booking without the ID
    const duplicatedBooking: Omit<Booking, 'id'> = {
      resourceId: booking.resourceId,
      projectId: booking.projectId,
      projectName: booking.projectName,
      costCentreId: booking.costCentreId,
      description: booking.description,
      startTime: booking.startTime,
      endTime: booking.endTime,
      color: booking.color,
      durationType: booking.durationType,
      halfDayPeriod: booking.halfDayPeriod,
      recurrence: booking.recurrence,
      billingClassification: booking.billingClassification,
      workType: booking.workType,
      taskDescription: booking.taskDescription,
      bookingNotes: booking.bookingNotes,
      status: booking.status,
      priority: booking.priority,
      attachments: booking.attachments,
      notifyReminders: booking.notifyReminders,
    };
    
    addBooking(duplicatedBooking);
    sonnerToast.success('Booking Duplicated', {
      description: 'A copy of the booking has been created.',
    });
  };

  const handleStatusChange = async (booking: Booking, newStatus: string, statusColor: string) => {
    try {
      if (filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(newStatus)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, newStatus]);
      }
      
      await updateBooking(booking.id, {
        ...booking,
        status: newStatus as Booking['status'],
        color: statusColor,
      });
      
      if (currentMode === 'live') refetchLiveBookings();
      
      const statusConfig = settings.statusConfigs.find(s => String(s.id) === String(newStatus));
      const statusDisplayName = statusConfig?.customText || statusConfig?.text || newStatus;
      
      sonnerToast.success('Status Updated', {
        description: `Booking status changed to "${statusDisplayName}"`,
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      sonnerToast.error('Failed to Update Status', {
        description: 'Please try again.',
      });
    }
  };

  const handleSlotClick = (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    
    // Use projectId from bookingData if provided (from BryntumSchedulerTimeline in project-resource mode)
    // This ensures the project is pre-selected when clicking on a day in project & resource view
    const projectIdToPreselect = bookingData?.projectId;
    
    // Scenario 2: Timeline click - set duration type based on calendar view
    // Day view: hourly booking with specific times
    // Non-day view: full-day booking (ignore Bryntum's time, use 00:00:00)
    if (calendarView === 'day') {
      // Day view: Add 15 minutes for end time
      let endHour: number;
      let endMinute: number;
      endMinute = startTime.minute + 15;
      if (endMinute >= 60) {
        endHour = startTime.hour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startTime.hour;
      }
      
      setPreselectedStartTime(startTime);
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect 
      });
    } else {
      // Non-day views: full-day booking - don't set times (use 00:00:00)
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect 
      });
    }
    
    setIsBookingModalOpen(true);
  };

  const handleDragSelect = (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    setPreselectedEndDate(endDate || date);
    setPreselectedStartTime(startTime);
    setPreselectedEndTime(endTime);
    
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && String(resource.id).includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = String(resource.id).split('-');
      if (parts.length >= 2 && !String(resource.id).startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    
    // Scenario 3 & 4b: Timeline drag - set duration type based on calendar view
    // Day view (Scenario 4b): hourly booking with times from timeline
    // Non-day view (Scenario 3): full-day booking (ignore Bryntum's time, use 00:00:00)
      if (calendarView === 'day' || bookingData?.durationType === 'hourly') {
        // Hourly selection (either Day view or zoomed hourly in other views): use dragged times
        setPreselectedStartTime(startTime);
        setPreselectedEndTime(endTime);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'hourly',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      } else {
        // Non-hourly in non-day views: treat as full-day, ignore times
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData({ 
          ...bookingData, 
          durationType: 'full-day',
          projectId: projectIdToPreselect || bookingData?.projectId 
        });
      }
    
    setIsBookingModalOpen(true);
  };

  const handlePasteBooking = (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    bookingData?: Booking
  ) => {
    if (!bookingData) {
      sonnerToast.error('No Booking to Paste', {
        description: 'Please copy a booking first.',
      });
      return;
    }
    
    // Calculate new start and end times based on the paste location
    const originalStart = new Date(bookingData.startTime);
    const originalEnd = new Date(bookingData.endTime);
    const durationMs = originalEnd.getTime() - originalStart.getTime();
    
    // For non-hourly bookings, use UTC dates at midnight (same logic as add booking)
    let finalStartTime: Date;
    let finalEndTime: Date;
    
    if (bookingData.durationType === 'full-day' || bookingData.durationType === 'half-day-am' || bookingData.durationType === 'half-day-pm') {
      // Extract the local date from the paste location
      const pasteYear = date.getFullYear();
      const pasteMonth = date.getMonth();
      const pasteDay = date.getDate();
      
      // Create UTC dates at midnight
      finalStartTime = new Date(Date.UTC(pasteYear, pasteMonth, pasteDay, 0, 0, 0, 0));
      
      // Calculate duration in days and add to start date
      const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
      finalEndTime = new Date(Date.UTC(pasteYear, pasteMonth, pasteDay + durationDays, 0, 0, 0, 0));
    } else {
      // For hourly bookings, preserve the original booking's time-of-day
      const newStartTime = new Date(date);
      newStartTime.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);
      finalStartTime = newStartTime;
      finalEndTime = new Date(newStartTime.getTime() + durationMs);
    }
    
    // Create a copy of the booking with new resource and times
    const pastedBooking: Omit<Booking, 'id'> = {
      resourceId: resource.id,
      projectId: bookingData.projectId,
      projectName: bookingData.projectName,
      costCentreId: bookingData.costCentreId,
      description: bookingData.description,
      startTime: formatDateAsLocalISO(finalStartTime),
      endTime: formatDateAsLocalISO(finalEndTime),
      color: bookingData.color,
      durationType: bookingData.durationType,
      halfDayPeriod: bookingData.halfDayPeriod,
      recurrence: bookingData.recurrence,
      billingClassification: bookingData.billingClassification,
      workType: bookingData.workType,
      taskDescription: bookingData.taskDescription,
      bookingNotes: bookingData.bookingNotes,
      status: bookingData.status,
      priority: bookingData.priority,
      attachments: bookingData.attachments,
      notifyReminders: bookingData.notifyReminders,
      bookingTypeId: bookingData.bookingTypeId,
      customer: bookingData.customer,
      location: bookingData.location,
      details: bookingData.details,
      bookingReference: bookingData.bookingReference,
      userChar: bookingData.userChar,
      userNumber: bookingData.userNumber,
      userFlag: bookingData.userFlag,
      userDate: bookingData.userDate,
      userTime: bookingData.userTime,
      userNotes: bookingData.userNotes,
    };
    
    addBooking(pastedBooking);
    
    // Show success toast
    sonnerToast.success('Booking Pasted', {
      description: `"${bookingData.projectName || bookingData.description || 'Booking'}" has been pasted to ${resource.name}.`,
    });
    
    // Clear the copied booking after paste
    setCopiedBooking(null);
  };

  const handleQuickAddBooking = (resource: Resource, date: Date, startTime?: string, endTime?: string) => {
    setQuickAddPreselectedDate(date);
    setQuickAddPreselectedResource(resource);
    if (startTime) {
      setQuickAddDefaultStartTime(startTime);
    }
    if (endTime) {
      setQuickAddDefaultEndTime(endTime);
    }
    // Extract project ID if in project-resource mode and store it for Quick Add modal
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    // Store project ID in preselected booking data for Quick Add modal to use
    setPreselectedBookingData({ projectId: projectIdToPreselect });
    
    setIsQuickAddModalOpen(true);
  };

  const handleQuickAddBookingSave = async (data: QuickAddBookingData) => {
    console.log('Quick Add Booking data:', data);
    
    try {
      // Check if we have a preselected resource
      if (!quickAddPreselectedResource) {
        sonnerToast.error('No Resource Selected', {
          description: 'Please select a resource to create a booking.',
        });
        return;
      }
      
      // Get the default booking type from settings
      const defaultBookingTypeId = settings.quickAddDefault;
      const defaultBookingType = settings.bookingTypes.find(bt => String(bt.id) === String(defaultBookingTypeId));
      
      // If no default booking type is set or found, use the first active booking type
      const bookingTypeId = defaultBookingType?.id || settings.bookingTypes.find(bt => bt.active)?.id || settings.bookingTypes[0]?.id;
      
      if (!bookingTypeId) {
        sonnerToast.error('Booking Type not assigned', {
          description: 'No booking type has been assigned. Please contact your administrator to configure booking type permissions.',
        });
        return;
      }
      
      // Prepare start and end times
      let startTime: string;
      let endTime: string;
      
      if (data.isFullDay) {
        // Full day booking - use UTC dates at midnight (00:00:00 UTC)
        // Extract local date values and create UTC dates
        const startDateObj = new Date(data.startDate);
        const startYear = startDateObj.getFullYear();
        const startMonth = startDateObj.getMonth();
        const startDay = startDateObj.getDate();
        
        const endDateObj = new Date(data.endDate);
        const endYear = endDateObj.getFullYear();
        const endMonth = endDateObj.getMonth();
        const endDay = endDateObj.getDate();
        
        // Create UTC dates at midnight - end date + 1 for exclusive end
        const utcStartDate = new Date(Date.UTC(startYear, startMonth, startDay, 0, 0, 0, 0));
        const utcEndDate = new Date(Date.UTC(endYear, endMonth, endDay + 1, 0, 0, 0, 0));
        
        startTime = utcStartDate.toISOString();
        endTime = utcEndDate.toISOString();
      } else {
        // Timed booking - combine date with time
        const [startHour, startMinute] = data.startTime!.split(':').map(Number);
        const startDate = new Date(data.startDate);
        startDate.setHours(startHour, startMinute, 0, 0);
        
        const [endHour, endMinute] = data.endTime!.split(':').map(Number);
        const endDate = new Date(data.endDate);
        endDate.setHours(endHour, endMinute, 0, 0);
        
        startTime = startDate.toISOString();
        endTime = endDate.toISOString();
      }
      
      // Get the preselected project ID only in project-resource view
      let selectedProject = null;
      
      // In project-resource view, use the preselected project
      if (groupingMode === 'project-resource' && preselectedBookingData?.projectId) {
        selectedProject = projectDetails.find(p => String(p.id) === String(preselectedBookingData.projectId));
        
        if (!selectedProject) {
          sonnerToast.error('No Projects Available', {
            description: 'Please create a project before adding bookings.',
          });
          return;
        }
      }
      // In resource view, leave project unset (null)
      
      // Extract the actual resource ID (in project-resource or customer mode, IDs are composite)
      let actualResourceId = quickAddPreselectedResource.id;
      if (groupingMode === 'project-resource' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-')) {
        const parts = actualResourceId.split('-');
        if (parts.length >= 2) {
          actualResourceId = parts.slice(1).join('-'); // Get everything after the first dash
        }
      } else if (groupingMode === 'customer' && actualResourceId.includes('-') && !actualResourceId.startsWith('group-') && !actualResourceId.startsWith('customer-')) {
        // Match by suffix to handle hyphens in customer names
        const matchedRes = resources.find(r => actualResourceId.endsWith(`-${String(r.id)}`));
        if (matchedRes) {
          actualResourceId = String(matchedRes.id);
        } else {
          const dashIdx = actualResourceId.indexOf('-');
          if (dashIdx !== -1) actualResourceId = actualResourceId.slice(dashIdx + 1);
        }
      }
      
      // Create booking for the preselected resource only
      const newBooking: Omit<Booking, 'id'> = {
        bookingTypeId: bookingTypeId != null ? String(bookingTypeId) : undefined, // Use the default booking type from settings
        resourceId: actualResourceId,
        resourceIds: [actualResourceId],
        projectId: selectedProject?.id != null ? String(selectedProject.id) : null,
        projectName: selectedProject?.name || '',
        clientId: selectedProject?.clientId ?? null, // Auto-populate client from project
        description: data.description || '',
        startTime,
        endTime,
        timezone: selectedTimezone,
        color: selectedProject?.color || '#3b82f6',
        durationType: data.isFullDay ? 'full-day' : 'hourly',
        billingClassification: 'billable',
        workType: 'onsite',
        status: 'confirmed',
        priority: 'medium',
        notifyReminders: false,
      };
      
      await addBooking(newBooking);
      
      // Close the modal
      setIsQuickAddModalOpen(false);
      
      // Reset preselected resource
      setQuickAddPreselectedResource(undefined);
      
      // Show success message
      const bookingTypeName = defaultBookingType?.name || 'booking';
      sonnerToast.success('Booking Created', {
        description: `Successfully created ${bookingTypeName} for ${quickAddPreselectedResource.name}.`,
      });
      
      console.log(`✅ Successfully created quick booking (${bookingTypeName}) for resource: ${quickAddPreselectedResource.name}`);
    } catch (error) {
      console.error('❌ Error creating quick booking:', error);
      sonnerToast.error('Failed to create booking', {
        description: 'Please try again.',
      });
    }
  };

  const handleSaveBooking = async (booking: Omit<Booking, 'id'>) => {
    setIsSavingBooking(true);
    console.log('🔵 handleSaveBooking called with booking:', booking);
    console.log('📋 ResourceIds:', booking.resourceIds);
    console.log('📅 Date Range:', { start: booking.startTime, end: booking.endTime });
    console.log('👤 Resource ID:', booking.resourceId, '(type:', typeof booking.resourceId, ')');
    console.log('🔁 Recurrence:', booking.recurrence);
    
    // ── Recurring booking creation ──────────────────────────────────────
    // The modal sets booking.recurrence (pattern, interval, etc.) but does NOT
    // generate individual occurrences. We detect recurrence here, generate dates,
    // create the first booking immediately, and process the rest in background.
    const hasRecurrence = booking.recurrence && booking.recurrence.pattern && booking.recurrence.pattern !== 'none';
    console.log('🔁 hasRecurrence:', hasRecurrence, '| pattern:', booking.recurrence?.pattern);

    if (hasRecurrence) {
      console.log('🔄 Detected recurring booking — generating occurrences');
      console.log('🔄 Recurrence settings:', JSON.stringify(booking.recurrence));

      const recurrenceGroupId = crypto.randomUUID();
      console.log('🔄 Generated recurrenceGroupId (UUID):', recurrenceGroupId);

      // Build the first (parent) booking — keeps recurrence settings for editing
      const parentBooking: Omit<Booking, 'id'> = {
        ...booking,
        recurrenceGroupId,
        isRecurrenceParent: true,
      };

      // Generate remaining occurrence dates (excludes the first/parent date)
      const occurrenceDates = generateRecurrenceDates(booking, booking as Booking);
      console.log('📆 Generated', occurrenceDates.length, 'occurrence dates:', occurrenceDates.map(d => d.toISOString()));

      try {
        // Step 1: Create the first booking immediately
        console.log('🚀 Creating first recurring booking immediately');
        let parentBookingId: number | undefined;
        if (parentBooking.resourceIds && parentBooking.resourceIds.length > 0) {
          console.log('🚀 Multi-resource parent: creating for', parentBooking.resourceIds.length, 'resources');
          const promises = parentBooking.resourceIds.map(async (resourceId) => {
            const individualBooking: Omit<Booking, 'id'> = {
              ...parentBooking,
              resourceId,
              resourceIds: parentBooking.resourceIds,
            };
            return addBooking(individualBooking);
          });
          const results = await Promise.all(promises);
          // Use the first created booking as the parent
          const firstResult = results.find(r => r != null);
          if (firstResult) parentBookingId = Number(firstResult.id);
        } else {
          console.log('🚀 Single-resource parent: resourceId =', parentBooking.resourceId);
          const createdParent = await addBooking(parentBooking);
          if (createdParent) parentBookingId = Number(createdParent.id);
        }
        console.log('🚀 Parent booking created with ID:', parentBookingId);
        console.log('✅ First recurring booking created successfully');

        // Single toast: confirm first booking + inform about background creation
        if (occurrenceDates.length > 0) {
          sonnerToast.success(
            `1 booking created successfully. ${occurrenceDates.length} recurring booking${occurrenceDates.length !== 1 ? 's' : ''} will be created in the background.`
          );

          // Step 2: Build all child bookings and send to server in a single batch request.
          // The server processes all bookings independently of the browser — no localStorage needed.
          const originalStart = new Date(booking.startTime);
          const originalEnd = new Date(booking.endTime);
          const duration = originalEnd.getTime() - originalStart.getTime();

          const childBookings: Omit<Booking, 'id'>[] = occurrenceDates.map(date => {
            const newStart = new Date(date);
            newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
            const newEnd = new Date(newStart.getTime() + duration);
            return {
              ...booking,
              recurrence: undefined,
              startTime: formatDateAsLocalISO(newStart),
              endTime: formatDateAsLocalISO(newEnd),
              recurrenceGroupId,
              isRecurrenceParent: false,
              parentBookingId: parentBookingId || undefined,
            };
          });

          // Fire-and-forget: server handles all creation even if the browser closes
          createBatchBookings(childBookings).then(() => {
            console.log('✅ Server batch creation complete');
            if (currentMode === 'live') refetchLiveBookings();
          }).catch(err => {
            console.error('❌ Server batch creation failed:', err);
          });
        } else {
          console.log('⚠️ No occurrence dates generated — only parent booking created');
          sonnerToast.success('Booking Created', {
            description: 'The booking has been successfully created.',
          });
        }

        // Reset preselected values after save
        setPreselectedResource(undefined);
        setPreselectedDate(undefined);
        setPreselectedEndDate(undefined);
        setPreselectedStartTime(undefined);
        setPreselectedEndTime(undefined);
        setPreselectedBookingData(undefined);
      } catch (error) {
        console.error('❌ Error creating recurring booking:', error);
        sonnerToast.error('Failed to create recurring booking', {
          description: 'Please try again.',
        });
      } finally {
        setIsSavingBooking(false);
      }
      return;
    }
    
    try {
      // Check if multiple resources are selected
      if (booking.resourceIds && booking.resourceIds.length > 0) {
        console.log(`🎯 Creating ${booking.resourceIds.length} bookings for multiple resources`);
        
        // Create a separate booking for each resource
        const bookingPromises = booking.resourceIds.map(async (resourceId) => {
          const individualBooking: Omit<Booking, 'id'> = {
            ...booking,
            resourceId, // Set the specific resource ID
            resourceIds: booking.resourceIds, // Keep the resourceIds array for editing later
          };
          
          console.log(`📝 Creating booking for resource: ${resourceId}`);
          return addBooking(individualBooking);
        });
        
        // Wait for all bookings to be created
        await Promise.all(bookingPromises);
        
        console.log(`✅ Successfully created ${booking.resourceIds.length} bookings`);
        sonnerToast.success('Bookings Created', {
          description: `Successfully created ${booking.resourceIds.length} booking${booking.resourceIds.length > 1 ? 's' : ''} across ${booking.resourceIds.length} resource${booking.resourceIds.length > 1 ? 's' : ''}.`,
        });
      } else {
        // Single resource booking (legacy behavior)
        await addBooking(booking);
        console.log('✅ Booking added successfully');
        sonnerToast.success('Booking Created', {
          description: 'The booking has been successfully created.',
        });
      }
      
      // Reset preselected values after save
      setPreselectedResource(undefined);
      setPreselectedDate(undefined);
      setPreselectedEndDate(undefined);
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData(undefined);
    } catch (error) {
      console.error('❌ Error adding booking:', error);
      sonnerToast.error('Failed to create booking', {
        description: 'Please try again.',
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Helper: check if recurrence options have changed between original and updated booking
  const hasRecurrenceOptionsChanged = (original: Booking, updated: Partial<Booking>): boolean => {
    if (!updated.recurrence && !original.recurrence) return false;
    if (!updated.recurrence || !original.recurrence) return true;
    const origRec = original.recurrence;
    const newRec = updated.recurrence;
    return (
      origRec.pattern !== newRec.pattern ||
      origRec.interval !== newRec.interval ||
      origRec.endType !== newRec.endType ||
      origRec.endDate !== newRec.endDate ||
      origRec.occurrences !== newRec.occurrences
    );
  };

  const handleUpdateBooking = async (id: string | number, booking: Partial<Booking>) => {
    setIsSavingBooking(true);
    console.log('🔵 handleUpdateBooking called with id:', id, 'editScope:', editScope, 'booking:', booking);
    
    try {
      // If status is being changed and there's an active status filter, add the new status to the filter
      if (booking.status && filters.bookingStatuses.length > 0 && !filters.bookingStatuses.includes(booking.status)) {
        updateFilter('bookingStatuses', [...filters.bookingStatuses, booking.status]);
      }
      
      // Find the original booking
      const originalBooking = bookings.find(b => String(b.id) === String(id));
      
      if (!originalBooking) {
        console.error('❌ Original booking not found:', id);
        sonnerToast.error('Booking not found', {
          description: 'Could not find the original booking.',
        });
        return;
      }

      // Determine if recurrence was removed (user cleared recurring info)
      const recurrenceRemoved = originalBooking.recurrenceGroupId && 
        (!booking.recurrence || booking.recurrence.pattern === 'none');

      // ── Case A: Non-recurring booking → becoming recurring ──
      if (!originalBooking.recurrenceGroupId && booking.recurrence && booking.recurrence.pattern !== 'none') {
        console.log('🔄 Converting single booking to recurring');

        const recurrenceGroupId = crypto.randomUUID();
        const updatedOriginal: Partial<Booking> = {
          ...booking,
          recurrenceGroupId,
          isRecurrenceParent: true,
        };
        await updateBooking(String(id), updatedOriginal);

        const occurrences = generateRecurrenceDates(booking, originalBooking);
        
        if (occurrences.length > 0) {
          sonnerToast.success(
            `Booking updated successfully. ${occurrences.length} recurring booking${occurrences.length !== 1 ? 's' : ''} will be created in the background.`
          );

          const mergedBooking = { ...originalBooking, ...booking };
          const originalStart = new Date(mergedBooking.startTime);
          const originalEnd = new Date(mergedBooking.endTime);
          const duration = originalEnd.getTime() - originalStart.getTime();

          const childBookings: Omit<Booking, 'id'>[] = occurrences.map(date => {
            const newStart = new Date(date);
            newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
            const newEnd = new Date(newStart.getTime() + duration);
            return {
              ...mergedBooking,
              recurrence: undefined,
              startTime: formatDateAsLocalISO(newStart),
              endTime: formatDateAsLocalISO(newEnd),
              recurrenceGroupId,
              isRecurrenceParent: false,
            };
          });

          createBatchBookings(childBookings).then(() => {
            console.log('✅ Server batch creation (from update) complete');
            if (currentMode === 'live') refetchLiveBookings();
          }).catch(err => {
            console.error('❌ Server batch creation (from update) failed:', err);
          });
        } else {
          sonnerToast.success('Booking Updated', {
            description: 'The booking has been successfully updated.',
          });
        }

        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case B: Recurring booking — editScope = 'this' (only this booking) ──
      if (originalBooking.recurrenceGroupId && editScope === 'this') {
        console.log('🔄 Editing only this recurring booking:', id);

        if (recurrenceRemoved) {
          // User removed recurrence → convert this booking to a normal (non-recurring) booking
          console.log('🔄 Removing recurrence from this booking — converting to normal booking');
          await updateBooking(String(id), {
            ...booking,
            recurrenceGroupId: undefined,
            isRecurrenceParent: false,
            parentBookingId: undefined,
            recurrence: undefined,
          });
        } else {
          // Update only this booking with the changed details
          await updateBooking(String(id), booking);
        }

        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
        if (currentMode === 'live') refetchLiveBookings();
        return;
      }

      // ── Case C: Recurring booking — editScope = 'all-future' ──
      if (originalBooking.recurrenceGroupId && editScope === 'all-future') {
        console.log('🔄 Editing this + all future bookings in group:', originalBooking.recurrenceGroupId);

        const bookingStart = new Date(originalBooking.startTime);
        // Get all future bookings in the group (including the current one)
        const futureGroupBookings = allBookings.filter(b => {
          if (b.recurrenceGroupId !== originalBooking.recurrenceGroupId) return false;
          return new Date(b.startTime) >= bookingStart;
        });
        // Get past bookings in the group (before the current one)
        const pastGroupBookings = allBookings.filter(b => {
          if (b.recurrenceGroupId !== originalBooking.recurrenceGroupId) return false;
          return new Date(b.startTime) < bookingStart;
        });

        console.log(`📋 Future bookings (including current): ${futureGroupBookings.length}, Past bookings: ${pastGroupBookings.length}`);

        if (recurrenceRemoved) {
          // User removed recurrence → remove recurring info from this + all future bookings
          console.log('🔄 Removing recurrence from this and all future bookings');
          for (const futureBooking of futureGroupBookings) {
            try {
              await updateBooking(futureBooking.id, {
                ...booking,
                recurrenceGroupId: undefined,
                isRecurrenceParent: false,
                parentBookingId: undefined,
                recurrence: undefined,
              });
            } catch (err) {
              console.error(`❌ Failed to update booking ${futureBooking.id}:`, err);
            }
          }
          sonnerToast.success('Bookings are updated successfully.');
          if (currentMode === 'live') refetchLiveBookings();
          return;
        }

        // Check if recurrence options have changed
        const recurrenceChanged = hasRecurrenceOptionsChanged(originalBooking, booking);

        if (recurrenceChanged && booking.recurrence) {
          // Recurrence options changed → delete future bookings, create new ones
          console.log('🔄 Recurrence options changed — deleting future bookings and creating new ones');

          // Delete all future bookings except the edited one
          for (const futureBooking of futureGroupBookings) {
            if (String(futureBooking.id) !== String(id)) {
              try {
                await deleteBooking(futureBooking.id);
              } catch (err) {
                console.error(`❌ Failed to delete future booking ${futureBooking.id}:`, err);
              }
            }
          }

          // Create a new recurrence group for the edited booking + new children
          const newGroupId = crypto.randomUUID();
          await updateBooking(String(id), {
            ...booking,
            recurrenceGroupId: newGroupId,
            isRecurrenceParent: true,
          });

          // Generate new recurring occurrences
          const occurrences = generateRecurrenceDates(booking, originalBooking);

          if (occurrences.length > 0) {
            const mergedBooking = { ...originalBooking, ...booking };
            const originalStart = new Date(mergedBooking.startTime);
            const originalEnd = new Date(mergedBooking.endTime);
            const duration = originalEnd.getTime() - originalStart.getTime();

            const childBookings: Omit<Booking, 'id'>[] = occurrences.map(date => {
              const newStart = new Date(date);
              newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
              const newEnd = new Date(newStart.getTime() + duration);
              return {
                ...mergedBooking,
                recurrence: undefined,
                startTime: formatDateAsLocalISO(newStart),
                endTime: formatDateAsLocalISO(newEnd),
                recurrenceGroupId: newGroupId,
                isRecurrenceParent: false,
              };
            });

            createBatchBookings(childBookings).then(() => {
              console.log('✅ Server batch recreation complete');
              if (currentMode === 'live') refetchLiveBookings();
            }).catch(err => {
              console.error('❌ Server batch recreation failed:', err);
            });
          }

          sonnerToast.success('Bookings are updated successfully.');
          if (currentMode === 'live') refetchLiveBookings();
          return;
        } else {
          // Recurrence options NOT changed → update this + all future bookings with new details
          console.log('🔄 Recurrence options unchanged — updating this and all future bookings');

          const originalStart = new Date(originalBooking.startTime);
          const originalEnd = new Date(originalBooking.endTime);
          const updatedStart = booking.startTime ? new Date(booking.startTime) : originalStart;
          const updatedEnd = booking.endTime ? new Date(booking.endTime) : originalEnd;
          const startDiff = updatedStart.getTime() - originalStart.getTime();
          const durationDiff = (updatedEnd.getTime() - updatedStart.getTime()) - (originalEnd.getTime() - originalStart.getTime());

          let updatedCount = 0;
          for (const futureBooking of futureGroupBookings) {
            if (String(futureBooking.id) === String(id)) {
              await updateBooking(String(id), booking);
              updatedCount++;
            } else {
              const groupUpdate: Partial<Booking> = { ...booking };
              
              if (startDiff !== 0 || durationDiff !== 0) {
                const siblingStart = new Date(futureBooking.startTime);
                if (startDiff !== 0) {
                  siblingStart.setHours(updatedStart.getHours(), updatedStart.getMinutes(), updatedStart.getSeconds(), updatedStart.getMilliseconds());
                }
                const newDuration = updatedEnd.getTime() - updatedStart.getTime();
                const newSiblingEnd = new Date(siblingStart.getTime() + newDuration);
                groupUpdate.startTime = formatDateAsLocalISO(siblingStart);
                groupUpdate.endTime = formatDateAsLocalISO(newSiblingEnd);
              } else {
                delete groupUpdate.startTime;
                delete groupUpdate.endTime;
              }
              
              try {
                await updateBooking(futureBooking.id, groupUpdate);
                updatedCount++;
              } catch (err) {
                console.error(`❌ Failed to update future booking ${futureBooking.id}:`, err);
              }
            }
          }

          sonnerToast.success('Bookings are updated successfully.');
          console.log(`✅ Recurring booking future update complete: ${updatedCount}/${futureGroupBookings.length}`);
          if (currentMode === 'live') refetchLiveBookings();
          return;
        }
      }
      
      // ── Case D: Multi-resource booking update (non-recurring) ──
      // Only use the delete-and-recreate path when there are truly multiple resources.
      // Single-resource edits should fall through to Case E (simple update) so that
      // audit history and booking identity are preserved.
      if (booking.resourceIds && booking.resourceIds.length > 1) {
        console.log(`🎯 Updating multi-resource booking with ${booking.resourceIds.length} resources`);
        
        const originalResourceIds = originalBooking.resourceIds || [originalBooking.resourceId];
        
        const relatedBookings = bookings.filter(b => 
          b.startTime === originalBooking.startTime &&
          b.endTime === originalBooking.endTime &&
          String(b.projectId) === String(originalBooking.projectId) &&
          originalResourceIds.includes(b.resourceId)
        );
        
        for (const relatedBooking of relatedBookings) {
          await deleteBooking(relatedBooking.id);
        }
        
        const bookingPromises = booking.resourceIds.map(async (resourceId) => {
          const individualBooking: Omit<Booking, 'id'> = {
            ...originalBooking,
            ...booking,
            resourceId,
            resourceIds: booking.resourceIds,
          };
          return addBooking(individualBooking);
        });
        
        await Promise.all(bookingPromises);
        
        sonnerToast.success('Bookings Updated', {
          description: `Successfully updated ${booking.resourceIds.length} bookings across ${booking.resourceIds.length} resources.`,
        });
      } else {
        // ── Case E: Single resource, non-recurring update ──
        console.log('📝 Updating single resource booking');
        await updateBooking(String(id), booking);
        if (currentMode === 'live') refetchLiveBookings();
        sonnerToast.success('Booking Updated', {
          description: 'The booking has been successfully updated.',
        });
      }
    } catch (error) {
      console.error('❌ Error updating booking:', error);
      sonnerToast.error('Failed to update booking', {
        description: 'Please try again.',
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  // Generate recurrence dates based on recurrence settings
  const generateRecurrenceDates = (booking: Partial<Booking>, originalBooking: Booking): Date[] => {
    const recurrence = booking.recurrence || originalBooking.recurrence;
    if (!recurrence || recurrence.pattern === 'none') {
      console.log('📆 generateRecurrenceDates: no recurrence or pattern is none');
      return [];
    }

    const startDate = booking.startTime ? new Date(booking.startTime) : new Date(originalBooking.startTime);
    const interval = recurrence.interval || 1;
    const dates: Date[] = [];
    const maxOccurrences = recurrence.occurrences || 52; // Default max 52 occurrences (1 year of weekly)
    const endDate = recurrence.endDate ? new Date(recurrence.endDate) : undefined;

    console.log('📆 generateRecurrenceDates:', {
      pattern: recurrence.pattern,
      interval,
      endType: recurrence.endType,
      startDate: startDate.toISOString(),
      endDate: endDate?.toISOString() || 'none',
      maxOccurrences,
    });

    // Safety: if endDate is in the past or before startDate, use a sensible default
    if (recurrence.endType === 'on-date' && endDate && endDate <= startDate) {
      console.warn('⚠️ generateRecurrenceDates: endDate is before or equal to startDate, defaulting to startDate + 3 months');
      const fallbackEnd = addMonths(startDate, 3);
      for (let i = 1; i < maxOccurrences; i++) {
        let nextDate: Date;
        switch (recurrence.pattern) {
          case 'daily': nextDate = addDays(startDate, i * interval); break;
          case 'weekly': nextDate = addDays(startDate, i * 7 * interval); break;
          case 'monthly': nextDate = addMonths(startDate, i * interval); break;
          default: return dates;
        }
        if (nextDate > fallbackEnd) break;
        dates.push(nextDate);
      }
      return dates;
    }

    for (let i = 1; i < maxOccurrences; i++) {
      let nextDate: Date;
      switch (recurrence.pattern) {
        case 'daily':
          nextDate = addDays(startDate, i * interval);
          break;
        case 'weekly':
          nextDate = addDays(startDate, i * 7 * interval);
          break;
        case 'monthly':
          nextDate = addMonths(startDate, i * interval);
          break;
        default:
          console.warn('⚠️ generateRecurrenceDates: unknown pattern', recurrence.pattern);
          return dates;
      }

      // Stop if past end date
      if (endDate && nextDate > endDate) break;
      // Stop if we've reached the occurrence limit for 'after' end type
      if (recurrence.endType === 'after' && recurrence.occurrences && dates.length >= recurrence.occurrences - 1) break;

      dates.push(nextDate);
    }

    console.log('📆 generateRecurrenceDates: generated', dates.length, 'dates');
    return dates;
  };

  const handleBookingDragEnd = (
    booking: Booking,
    originalResource: Resource,
    newResourceId: string | number,
    newStartTime: Date,
    newEndTime: Date,
    isResize: boolean = false
  ) => {
    console.log('🔵 handleBookingDragEnd called:', {
      bookingId: booking.id,
      originalResourceId: originalResource.id,
      newResourceId,
      newStartTime: newStartTime.toISOString(),
      newEndTime: newEndTime.toISOString(),
      currentView: calendarView,
      currentDurationType: booking.durationType,
    });

    // Convert newResourceId to string for string operations (handles INT IDs after schema change)
    const newResourceIdStr = String(newResourceId);

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-", "customer-", etc.
    if (newResourceIdStr.startsWith('group-') || newResourceIdStr.startsWith('customer-')) {
      console.log('❌ Invalid drop target: Cannot drop booking on group node', {
        bookingId: booking.id,
        groupId: newResourceId,
      });
      
      // Cancel drag and show error message
      cancelDrag();
      
      sonnerToast.error('Invalid Drop Location', {
        description: 'Please drop the booking on a specific resource.',
      });
      
      return;
    }

    // Find resource by comparing as strings (handles INT IDs after schema change)
    const newResource = resources.find(r => String(r.id) === newResourceIdStr);
    if (!newResource) {
      // Cancel drag if resource not found
      cancelDrag();
      return;
    }

    const stripTzSuffix = (t: string) => t.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    const originalStartTime = new Date(stripTzSuffix(booking.startTime));
    const originalEndTime = new Date(stripTzSuffix(booking.endTime));

    // Apply date/time logic based on duration type
    // For non-hourly bookings: start at 00:00:00 UTC, end at (end date + 1 day) 00:00:00 UTC
    // This handles both drag (move) and resize operations correctly
    let adjustedStartTime: Date;
    let adjustedEndTime: Date;
    
    // Determine if this is a full-day type booking (not hourly)
    const isFullDayType = booking.durationType === 'full-day' || 
                          booking.durationType === 'half-day-am' || 
                          booking.durationType === 'half-day-pm' ||
                          booking.durationType === 'half-day';
    
    const originalStart = new Date(stripTzSuffix(booking.startTime));
    const originalEnd = new Date(stripTzSuffix(booking.endTime));
    const hasSpecificTime = originalStart.getHours() !== 0 || originalStart.getMinutes() !== 0;
    
    // Treat as hourly if: durationType is 'hourly' OR (durationType is not full-day AND has specific times)
    const isHourlyBooking = booking.durationType === 'hourly' || (!isFullDayType && hasSpecificTime);
    
    console.log('📅 Booking type detection:', {
      durationType: booking.durationType,
      isFullDayType,
      hasSpecificTime,
      isHourlyBooking,
      originalStartHours: originalStart.getHours(),
      originalStartMinutes: originalStart.getMinutes(),
      calendarView,
    });

    if (!isHourlyBooking) {
      // For non-hourly (full-day) bookings: store start at 00:00:00, end at 23:59:59.999
      // Extract the LOCAL date values from Bryntum (what the user sees in the calendar)
      const startYear = newStartTime.getFullYear();
      const startMonth = newStartTime.getMonth();
      const startDay = newStartTime.getDate();
      
      adjustedStartTime = new Date(startYear, startMonth, startDay, 0, 0, 0, 0);
      
      // Bryntum uses EXCLUSIVE end dates: a 1-day booking on March 13 has
      // endDate = March 14 00:00. We need to convert to the INCLUSIVE last day
      // at 23:59:59.999 so the stored endTime reflects the actual last day.
      if (newEndTime.getHours() === 0 && newEndTime.getMinutes() === 0 && newEndTime.getSeconds() === 0) {
        const inclusiveEnd = new Date(newEndTime);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        adjustedEndTime = new Date(inclusiveEnd.getFullYear(), inclusiveEnd.getMonth(), inclusiveEnd.getDate(), 23, 59, 59, 999);
      } else {
        const endYear = newEndTime.getFullYear();
        const endMonth = newEndTime.getMonth();
        const endDay = newEndTime.getDate();
        adjustedEndTime = new Date(endYear, endMonth, endDay, 23, 59, 59, 999);
      }
      
      console.log('📅 Non-hourly booking - adjusting times:', {
        originalStart: newStartTime.toISOString(),
        originalEnd: newEndTime.toISOString(),
        adjustedStart: adjustedStartTime.toISOString(),
        adjustedEnd: adjustedEndTime.toISOString(),
      });
    } else {
      // For hourly bookings: Use the times directly from Bryntum without any conversion
      // Whatever time the user sees/selects is what gets stored
      
      if (isResize || calendarView === 'day') {
        // Resize operation OR Day view drag - use exact times from Bryntum
        // Just pass through the times directly - no conversion
        adjustedStartTime = new Date(newStartTime);
        adjustedEndTime = new Date(newEndTime);
        
        console.log(`📅 Hourly booking ${isResize ? 'RESIZE' : 'day view drag'} - using times directly:`, {
          startTime: adjustedStartTime.toISOString(),
          endTime: adjustedEndTime.toISOString(),
          isResize,
        });
      } else {
        // Non-day view DRAG - preserve original time, change only the date
        const durationMs = originalEnd.getTime() - originalStart.getTime();
        
        // Get the original time components
        const originalHours = originalStart.getHours();
        const originalMinutes = originalStart.getMinutes();
        
        // Create new date with the dropped date but original time
        adjustedStartTime = new Date(newStartTime);
        adjustedStartTime.setHours(originalHours, originalMinutes, 0, 0);
        
        // End time is start time + original duration
        adjustedEndTime = new Date(adjustedStartTime.getTime() + durationMs);
        
        console.log('📅 Hourly booking DRAG in non-day view - preserving original time:', {
          originalHours,
          originalMinutes,
          adjustedStart: adjustedStartTime.toISOString(),
          adjustedEnd: adjustedEndTime.toISOString(),
        });
      }
    }

    // Check if anything actually changed (compare as strings for type safety)
    const hasChanged = 
      String(originalResource.id) !== newResourceIdStr ||
      originalStartTime.getTime() !== adjustedStartTime.getTime() ||
      originalEndTime.getTime() !== adjustedEndTime.getTime();

    if (!hasChanged) {
      // Cancel drag if nothing changed
      cancelDrag();
      return;
    }

    // Prepare the update object with adjusted times
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      resourceId: newResource.id,
      startTime: formatDateAsLocalISO(adjustedStartTime),
      endTime: formatDateAsLocalISO(adjustedEndTime),
    };

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    } else if (isHourlyBooking) {
      bookingUpdate.durationType = 'hourly';
    }

    // Check if confirmation dialog should be shown based on user preference
    // User preference takes priority, falls back to global setting
    const shouldConfirm = settings.userConfirmDragDrop ?? settings.confirmDragDrop;
    
    if (shouldConfirm) {
      // Show confirmation dialog - cancel drag so booking returns to original position
      cancelDrag();
      
      console.log('🟡 Setting pending drag update (confirmation required):', {
        bookingId: booking.id,
        newResourceId: newResource.id,
        adjustedStartTime: adjustedStartTime.toISOString(),
        adjustedEndTime: adjustedEndTime.toISOString(),
        durationType: bookingUpdate.durationType,
      });

      // Show confirmation dialog with pending update details
      // Use adjusted times so the dialog shows the correct dates
      setPendingDragUpdate({
        booking,
        originalResource,
        newResource,
        originalStartTime,
        originalEndTime,
        newStartTime: adjustedStartTime,
        newEndTime: adjustedEndTime,
      });
      setShowDragConfirm(true);
    } else {
      // Apply drag immediately without confirmation
      console.log('✅ Applying drag immediately (no confirmation required):', {
        bookingId: booking.id,
        newResourceId: newResource.id,
        newStartTime: newStartTime.toISOString(),
        newEndTime: newEndTime.toISOString(),
        durationType: bookingUpdate.durationType,
      });

      updateBooking(booking.id, bookingUpdate).then(() => {
        if (currentMode === 'live') refetchLiveBookings();
      });

      sonnerToast.success('Booking Moved', {
        description: 'The booking has been successfully moved.',
      });
    }
  };

  const handleConfirmDrag= () => {
    if (!pendingDragUpdate) return;

    // newStartTime and newEndTime are already adjusted (UTC times) from handleBookingDragEnd
    const { booking, newResource, newStartTime, newEndTime } = pendingDragUpdate;

    console.log('🟢 handleConfirmDrag - Updating booking:', {
      bookingId: booking.id,
      originalResourceId: booking.resourceId,
      newResourceId: newResource.id,
      originalStartTime: booking.startTime,
      originalEndTime: booking.endTime,
      newStartTime: newStartTime.toISOString(),
      newEndTime: newEndTime.toISOString(),
      currentView: calendarView,
      currentDurationType: booking.durationType,
    });

    // Prepare the update object - times are already adjusted in handleBookingDragEnd
    // Preserve all existing booking fields to prevent data loss (e.g., status)
    // Use formatDateAsLocalISO to prevent timezone conversion (e.g., -5:30 for IST)
    const bookingUpdate: Partial<Booking> = {
      ...booking,
      resourceId: newResource.id,
      startTime: formatDateAsLocalISO(newStartTime),
      endTime: formatDateAsLocalISO(newEndTime),
    };

    if (calendarView === 'day') {
      bookingUpdate.durationType = 'hourly';
    } else if (booking.durationType) {
      bookingUpdate.durationType = booking.durationType;
    }

    // Update the booking - this will trigger a smooth transition
    console.log('✅ Updating booking with:', bookingUpdate);
    updateBooking(booking.id, bookingUpdate).then(() => {
      if (currentMode === 'live') refetchLiveBookings();
    });

    sonnerToast.success('Booking Moved', {
      description: 'The booking has been successfully moved.',
    });

    // Clean up
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
  };

  const handleDiscardDrag = () => {
    // Revert the booking to its original position in Bryntum's store
    if (pendingDragUpdate && schedulerTimelineRef.current) {
      schedulerTimelineRef.current.revertEvent(
        pendingDragUpdate.booking.id,
        pendingDragUpdate.originalResource.id,
        pendingDragUpdate.originalStartTime,
        pendingDragUpdate.originalEndTime
      );
    }
    
    // Clean up the dialog state
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
    
    sonnerToast.info('Changes Discarded', {
      description: 'The booking was not moved.',
    });
  };

  const getDateLabel = () => {
    switch (calendarView) {
      case 'day':
        return format(selectedDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        return `Week of ${format(selectedDate, 'MMM d, yyyy')}`;
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      case 'quarter':
        return `Q${Math.floor(selectedDate.getMonth() / 3) + 1} ${format(selectedDate, 'yyyy')}`;
      case 'year':
        return format(selectedDate, 'yyyy');
      default:
        return '';
    }
  };

  const handleDatePickerSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setIsDatePickerOpen(false);
    }
  };

  // Handler for Copy Booking from context menu
  const handleCopyBooking = (booking: Booking) => {
    setCopiedBooking(booking);
    sonnerToast.success('Booking Copied', {
      description: `"${booking.projectName || 'Booking'}" has been copied. Right-click on an empty cell to paste.`,
    });
  };

  // Handler for Add Booking from context menu - opens the booking form
  const handleAddBookingFromMenu = (resource: Resource, date: Date) => {
    setPreselectedResource(resource);
    setPreselectedDate(date);
    setPreselectedEndDate(date);
    
    // Extract project ID if in project-resource mode
    let projectIdToPreselect: string | undefined;
    if (groupingMode === 'project-resource' && resource.id.includes('-')) {
      // In project-resource mode, resource IDs are in format "projectId-resourceId"
      const parts = resource.id.split('-');
      if (parts.length >= 2 && !resource.id.startsWith('group-')) {
        projectIdToPreselect = parts[0];
      }
    }
    
    // Set duration type based on calendar view
    if (calendarView === 'day') {
      // Day view: Use the clicked time slot and add 15 minutes for end time
      const startHour = date.getHours();
      const startMinute = date.getMinutes();
      
      // Calculate end time: 15 minutes after start time
      let endHour: number;
      let endMinute: number;
      endMinute = startMinute + 15;
      if (endMinute >= 60) {
        endHour = startHour + 1;
        endMinute = endMinute - 60;
      } else {
        endHour = startHour;
      }
      
      setPreselectedStartTime({ hour: startHour, minute: startMinute });
      setPreselectedEndTime({ hour: endHour, minute: endMinute });
      setPreselectedBookingData({ 
        durationType: 'hourly',
        projectId: projectIdToPreselect 
      });
    } else {
      setPreselectedStartTime(undefined);
      setPreselectedEndTime(undefined);
      setPreselectedBookingData({ 
        durationType: 'full-day',
        projectId: projectIdToPreselect 
      });
    }
    
    setBookingModalMode('create');
    setEditingBooking(undefined);
    setIsBookingModalOpen(true);
  };

  const renderCalendarView = () => {
    return (
      <BryntumSchedulerTimeline
        ref={schedulerTimelineRef}
        selectedDate={selectedDate}
        calendarView={calendarView}
        zoomLevel={zoomLevel}
        selectedTimezone={selectedTimezone}
        enableTimezones={settings.enableTimezones}
        resources={filteredData.filteredResources}
        bookings={filteredData.filteredBookings}
        resourceTimeRanges={resourceTimeRanges}
        timeRanges={timeRanges}
        groupingMode={groupingMode}

        schedulerGroups={schedulerGroups}
        statusConfigs={settings.statusConfigs}
        isHeatmapMode={isHeatmapMode}
        absences={mockAbsences}
        bankHolidays={mockBankHolidays}
        searchQuery={bookingSearchQuery}
        onEditBooking={handleEditBooking}
        onResolveConflict={handleResolveConflict}
        onDeleteBooking={handleDeleteBooking}
        onDuplicateBooking={handleDuplicateBooking}
        onMoveBooking={handleMoveBooking}
        onStatusChange={handleStatusChange}
        onCopyBooking={handleCopyBooking}
        onSlotClick={handleSlotClick}
        onDragSelect={handleDragSelect}
        onBookingDragEnd={handleBookingDragEnd}
        onAddBooking={handleAddBookingFromMenu}
        onQuickAddBooking={handleQuickAddBooking}
        onPasteBooking={handlePasteBooking}
        copiedBooking={copiedBooking}
        whatIfScenario={whatIfScenario}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header Controls */}
      <div className="shrink-0 border-b bg-background">
        <div className="flex items-center justify-between px-3 py-2 gap-2 w-full">
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="text-base font-semibold min-w-[140px] max-w-[200px] text-left hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors whitespace-nowrap"
                title="Click to select a date"
              >
                {getDateLabel()}
                <CalendarIcon className="h-4 w-4 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDatePickerSelect}
                defaultMonth={selectedDate}
              />
            </PopoverContent>
          </Popover>

          {/* Booking Quick Search */}
            <div className="relative w-[160px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={bookingSearchQuery}
                onChange={(e) => setBookingSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
              {bookingSearchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setBookingSearchQuery('')}
                  title="Clear search"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Data Mode Toggle */}
          <DataModeToggle screen="calendar" />

          {/* Booking Search Results Counter */}
          {bookingSearchQuery && (
            <Badge variant="secondary" className="h-8 px-3 text-sm font-semibold whitespace-nowrap">
              {filteredData.filteredBookings.length} / {bookings.length} Bookings
            </Badge>
          )}

          {/* Create Booking Button */}
          <Button onClick={handleCreateBooking} size="sm" className="h-8 px-2.5 gap-1" data-tour="create-booking">
            <Plus className="h-4 w-4" />
            {currentMode === 'demo' ? 'Add Booking' : 'New Booking'}
          </Button>

          {/* Current Timezone Selector - Shows next to Create Booking button */}
          <CurrentTimezoneSelector
            selectedTimezone={selectedTimezone}
            onTimezoneChange={handleTimezoneChange}
          />

          {/* Zoom Controls */}
          <div className="flex items-center gap-1.5">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomOut}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === 0}
              className="h-8 px-2"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[50px] text-center">
              {getZoomLabels()[zoomLevel]}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleZoomIn}
              disabled={calendarView === 'day' || calendarView === 'year' || calendarView === 'quarter' || zoomLevel === getMaxZoomLevel()}
              className="h-8 px-2"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>

          {/* View Selector */}
          <Select value={calendarView} onValueChange={(value) => setCalendarView(value as CalendarView)}>
            <SelectTrigger className="w-[100px] h-8" data-tour="calendar-view">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[240px]" position="popper" sideOffset={4} align="start">
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>
          {/* Filter Drawer Trigger — far right, aligns with right-side slide-in */}
          <CalendarFilterDrawerTrigger
            activeFilterCount={
              filters.departments.length +
              filters.resources.length +
              filters.roles.length +
              filters.projects.length +
              filters.costCentres.length +
              filters.bookingTypes.length +
              filters.bookingStatuses.length +
              (filters.utilisationRange[0] !== 0 || filters.utilisationRange[1] !== 100 || filters.overUtilisedOnly ? 1 : 0) +
              (hasActiveAdvancedFilters ? advancedFilters.conditions.length : 0)
            }
            onClick={() => setIsFilterTrayOpen(true)}
          />
        </div>
        </div>
      </div>

      {/* Filter Drawer (replaces collapsible tray) */}
      <CalendarFilterDrawer
        open={isFilterTrayOpen}
        onOpenChange={setIsFilterTrayOpen}
      />

      {/* ── Burnout risk banners — rendered via BurnoutRiskPanel below (portal slot) ── */}
      {isHeatmapMode && whatIfEnabled && (
        <div id="burnout-banner-slot" className="shrink-0" />
      )}

      {/* ── What-if amber banner — shown in calendar view when a scenario is active ── */}
      {whatIfScenario && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '7px 16px',
          flexShrink: 0,
          background: 'linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.04) 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.30)',
          borderLeft: '3px solid #f59e0b',
        }}>
          {/* Left: icon + what-if pill + description */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              backgroundColor: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 800,
            }}>⟳</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '2px 9px', borderRadius: 20, flexShrink: 0,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
              textTransform: 'uppercase' as const,
              backgroundColor: '#f59e0b', color: '#fff',
            }}>
              What-if mode
            </span>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#78350f', lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {whatIfScenario.type === 'rebalance'
                ? `${whatIfScenario.projectName ?? 'Project'} — move from ${whatIfScenario.atRiskResourceName ?? '…'} to ${whatIfScenario.candidateResourceName ?? '…'} · not yet saved`
                : `${whatIfScenario.freeResourceName ?? 'Resource'} becomes free W${whatIfScenario.freeFromWeek ?? '?'} · assign new work · not yet saved`
              }
            </span>
          </div>
          {/* Right: Commit + Discard buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              onClick={handleWhatIfCommit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                backgroundColor: '#0d9488', color: '#fff',
                border: 'none', letterSpacing: '0.01em',
                transition: 'opacity 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            >
              ✓ Commit changes
            </button>
            <button
              onClick={handleWhatIfDiscard}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '5px 14px', borderRadius: 6, flexShrink: 0,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                backgroundColor: 'transparent', color: '#ef4444',
                border: '1px solid #ef4444',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap' as const,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239,68,68,0.06)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
            >
              Discard scenario
            </button>
          </div>
        </div>
      )}

      {/* Calendar View + (optionally) Burnout Suggestions Panel side-by-side */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main calendar grid */}
        <div
          ref={schedulerDomRef}
          className="flex-1 overflow-hidden calendar-grid-container relative"
        >
          {!isCompanyLoading && !hasCompanyAccess ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-sm">
                First create at least one company and assign the access to the user.
              </p>
            </div>
          ) : (
            <>
              {/* Grouping mode selector — absolutely positioned over Bryntum's column header.
                  Pure React inside our own DOM tree, so it's always visible and interactive. */}
              {/* Grouping Select — spans the full 45px header height so it vertically
                  centres to match the collapse button. Measurements:
                    column=240px, cell-padding-left=12px, cell-padding-right=12px
                    button=32px → button sits at right: 12px (right edge = 228px from left)
                    gap=8px → select right edge = 228-8=220px
                    select left = 8px → select width = 220-8 = 212px                    */}
              <div className="absolute z-50 flex items-center" style={{ top: groupingSelectTop, left: 8, width: 170, height: 32 }}>
                <Select
                  value={groupingMode}
                  onValueChange={(mode) => {
                    setGroupingMode(mode as typeof groupingMode);
                    setHasUserChangedGrouping(true);
                  }}
                >
                  <SelectTrigger className="h-8 w-full text-[13px] text-foreground border-0 bg-transparent shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 font-normal hover:bg-accent transition-colors [&>span]:truncate">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="project-resource">Project &amp; Resource</SelectItem>
                    <SelectItem value="resource">Resource</SelectItem>
                    <SelectItem value="role-resource">Role &amp; Resource</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {renderCalendarView()}
              {(isSavingBooking || isLoadingBookings || isContextLoading || isGroupsLoading) && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center">
                  <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm font-medium">{isSavingBooking ? 'Saving booking...' : (isContextLoading || isGroupsLoading) ? 'Loading data...' : 'Loading bookings...'}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Burnout risk suggestions panel — fixed width, only in heatmap mode and when feature is enabled */}
        {isHeatmapMode && whatIfEnabled && onNavigateToCalendarWithWhatIf && (
          <BurnoutRiskPanelWrapper
            schedulerDomRef={schedulerDomRef}
            resources={filteredData.filteredResources}
            bookings={filteredData.filteredBookings}
            globalWorkingProfile={settings.globalWorkingProfile}
            onNavigateToCalendar={onNavigateToCalendarWithWhatIf}
          />
        )}
      </div>

      {/* Heatmap Legend — sits flush below the grid, only in heatmap mode */}
      <HeatmapLegend visible={isHeatmapMode} />

      {/* Comprehensive Booking Modal */}
      <ComprehensiveBookingModal
        key="comprehensive-booking-modal"
        open={isBookingModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsBookingModalOpen(false);
            // Ensure any temporary/phantom events are removed from the timeline
            schedulerTimelineRef.current?.cleanupPhantomEvents?.();
            // Reset preselected values when modal closes
            setBookingModalMode('create');
            setEditingBooking(undefined);
            setPreselectedResource(undefined);
            setPreselectedDate(undefined);
            setPreselectedEndDate(undefined);
            setPreselectedStartTime(undefined);
            setPreselectedEndTime(undefined);
            setPreselectedBookingData(undefined);
          }
        }}
        onSave={handleSaveBooking}
        onUpdate={handleUpdateBooking}
        onDelete={handleDeleteBooking}
        resources={resources}
        projectDetails={projectDetails}
        costCentres={costCentres}
        existingBookings={allBookings}
                preselectedResource={preselectedResource}
                preselectedDate={preselectedDate}
                preselectedEndDate={preselectedEndDate}
                preselectedStartTime={preselectedStartTime ? `${String(preselectedStartTime.hour).padStart(2, '0')}:${String(preselectedStartTime.minute).padStart(2, '0')}` : undefined}
        preselectedEndTime={preselectedEndTime ? `${String(preselectedEndTime.hour).padStart(2, '0')}:${String(preselectedEndTime.minute).padStart(2, '0')}` : undefined}
        preselectedBookingData={preselectedBookingData}
        editingBooking={editingBooking}
        mode={bookingModalMode}
        dataMode={currentMode}
      />

      {/* Drag Confirmation Dialog */}
      <BookingDragConfirmDialog
        open={showDragConfirm}
        booking={pendingDragUpdate?.booking || null}
        originalResource={pendingDragUpdate?.originalResource}
        newResource={pendingDragUpdate?.newResource}
        originalStartTime={pendingDragUpdate?.originalStartTime || null}
        originalEndTime={pendingDragUpdate?.originalEndTime || null}
        newStartTime={pendingDragUpdate?.newStartTime || null}
        newEndTime={pendingDragUpdate?.newEndTime || null}
        onConfirm={handleConfirmDrag}
        onDiscard={handleDiscardDrag}
      />

      {/* Quick Add Booking Modal */}
      <QuickAddBookingModal
        open={isQuickAddModalOpen}
        onClose={() => setIsQuickAddModalOpen(false)}
        onSave={handleQuickAddBookingSave}
        defaultStartTime={quickAddDefaultStartTime}
        defaultEndTime={quickAddDefaultEndTime}
        preselectedDate={quickAddPreselectedDate}
      />

      {/* Delete Booking Confirmation Dialog */}
      <DeleteBookingConfirmDialog
        open={showDeleteConfirm}
        booking={bookingToDelete}
        resource={bookingToDelete ? resources.find(r => String(r.id) === String(bookingToDelete.resourceId)) : null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onDeleteOnlyThis={handleDeleteOnlyThis}
        onDeleteAllFuture={handleDeleteAllFuture}
      />

      {/* Edit Recurring Booking Confirmation Dialog */}
      <EditBookingConfirmDialog
        open={showEditConfirm}
        onCancel={handleCancelEditConfirm}
        onEditThisBooking={handleEditThisBooking}
        onEditAllFutureBookings={handleEditAllFutureBookings}
      />

      {/* Move Booking Dialog */}
      <MoveBookingDialog
        open={showMoveDialog}
        onClose={handleCancelMove}
        booking={bookingToMove}
        resources={resources}
        onMove={handleConfirmMove}
      />

      {/* Resolve Conflict — Batch Assignment Modal */}
      {bookingsToResolve.length > 0 && (
        <AIBatchSuggestModal
          open={showResolveDialog}
          onOpenChange={(open) => {
            setShowResolveDialog(open);
            if (!open) setBookingsToResolve([]);
          }}
          bookings={bookingsToResolve as unknown as import('@/hooks/useUnallocatedBookings').UnallocatedBooking[]}
          resources={resources}
          existingBookings={allBookings}
          projects={projectDetails as unknown as import('@/types').Project[]}
          onConfirm={handleResolveConfirm}
        />
      )}
    </div>
  );
}
